import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import VoiceHoldMusicPicker from "./VoiceHoldMusicPicker";
import {
  deriveVoiceFirstResponder,
  validateTwilioPhoneRoutingQueue,
} from "./twilioPhoneRoutingGuards";

const titleOf = (o) => o?.nome ?? o?.name ?? o?.label ?? `#${o?.id ?? ""}`;

function multiSelectRenderValue(ids, labels, emptyLabel) {
  if (!ids.length) return emptyLabel;
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

function formatWaitHuman(sec, t) {
  const n = Number(sec) || 0;
  if (n >= 60 && n % 60 === 0) {
    const min = n / 60;
    return t("channelForm.twilioPhone.waitMinutes", {
      count: min,
      defaultValue: min === 1 ? "1 minuto" : "{{count}} minutos",
    });
  }
  return t("channelForm.twilioPhone.waitSeconds", {
    sec: n,
    defaultValue: "{{sec}} segundos",
  });
}

/**
 * Roteamento inicial + fila humana do canal Telefone Xpro.
 */
export default function TwilioPhoneRoutingQueueSection({
  t,
  formData,
  setFormData,
  idChatbotRef,
  idAgenteRef,
  loadingUsers,
  activeHumanUsers,
  allHumanUsers,
  chatbotMenuOptions,
  selectedChatbotOption,
  agentsList,
  agentSelectValue,
  queuePositions,
  queuePositionsLoading,
  hasHumanRouting,
  hasChatbotRouting,
  hasAgentRouting,
}) {
  const humanOptions = activeHumanUsers.length ? activeHumanUsers : allHumanUsers;
  const noneLabel = t("channelForm.twilioPhone.queueNoneSelected", "Nenhum");
  const activeAgents = (agentsList || []).filter(
    (a) =>
      a.status === true ||
      a.status === "active" ||
      a.enabled === true ||
      a.is_active === true
  );
  const hasChatbotOptions = (chatbotMenuOptions || []).length > 0;
  const hasAgentOptions = activeAgents.length > 0;

  const queueUserIds = Array.isArray(formData.voice_queue_user_ids)
    ? formData.voice_queue_user_ids.map(Number).filter((n) => n > 0)
    : [];
  const queueCargoIds = Array.isArray(formData.voice_queue_cargo_ids)
    ? formData.voice_queue_cargo_ids.map(Number).filter((n) => n > 0)
    : [];
  const hasQueuePool = queueUserIds.length > 0 || queueCargoIds.length > 0;
  const dispatchMode =
    String(formData.voice_queue_dispatch_mode || "auto").toLowerCase() === "claim"
      ? "claim"
      : "auto";
  const maxWaitSec =
    Number(formData.voice_queue_max_wait_sec) > 0
      ? Number(formData.voice_queue_max_wait_sec)
      : 300;
  const waitLabel = formatWaitHuman(maxWaitSec, t);
  const firstResponder = deriveVoiceFirstResponder(formData);
  const firstHumanId = Number(formData.idusuarios?.[0] || formData.idusuario || 0) || "";
  const firstHumanName =
    humanOptions.find((u) => Number(u.id) === Number(firstHumanId)) != null
      ? titleOf(humanOptions.find((u) => Number(u.id) === Number(firstHumanId)))
      : "";
  const chatbotName =
    selectedChatbotOption?.name ||
    selectedChatbotOption?.nome ||
    "";
  const agentName =
    activeAgents.find((a) => String(a.id) === String(agentSelectValue))?.name ||
    activeAgents.find((a) => String(a.id) === String(agentSelectValue))?.nome ||
    "";

  useEffect(() => {
    if (formData._voice_first_responder) return;
    setFormData((prev) =>
      prev._voice_first_responder
        ? prev
        : { ...prev, _voice_first_responder: deriveVoiceFirstResponder(prev) }
    );
  }, [formData._voice_first_responder, setFormData]);

  const onFirstResponderRadio = (value) => {
    if (value === "chatbot" && !hasChatbotOptions) return;
    if (value === "agent" && !hasAgentOptions) return;
    if (value === "none") {
      idChatbotRef.current = null;
      idAgenteRef.current = null;
      setFormData((prev) => ({
        ...prev,
        _voice_first_responder: "none",
        id_chatbot: null,
        id_agente: null,
        idusuarios: [],
        idusuario: null,
      }));
      return;
    }
    if (value === "human") {
      idChatbotRef.current = null;
      idAgenteRef.current = null;
      setFormData((prev) => ({
        ...prev,
        _voice_first_responder: "human",
        id_chatbot: null,
        id_agente: null,
        idusuarios: Array.isArray(prev.idusuarios)
          ? prev.idusuarios.slice(0, 1)
          : [],
        idusuario:
          Array.isArray(prev.idusuarios) && prev.idusuarios[0]
            ? prev.idusuarios[0]
            : null,
      }));
      return;
    }
    if (value === "chatbot") {
      idAgenteRef.current = null;
      setFormData((prev) => ({
        ...prev,
        _voice_first_responder: "chatbot",
        id_agente: null,
        idusuarios: [],
        idusuario: prev.id_chatbot != null ? Number(prev.id_chatbot) : null,
      }));
      return;
    }
    if (value === "agent") {
      idChatbotRef.current = null;
      setFormData((prev) => ({
        ...prev,
        _voice_first_responder: "agent",
        id_chatbot: null,
        idusuarios: [],
        idusuario: null,
      }));
    }
  };

  const queueNeeded =
    firstResponder === "chatbot" ||
    firstResponder === "agent" ||
    firstResponder === "none";
  const showHoldExperience = hasQueuePool || firstResponder === "human";
  const showDispatch = hasQueuePool;
  const showWait = hasQueuePool || firstResponder === "human";
  const showLastResort = firstResponder !== "human";
  const lastResortFilled = String(formData.telefonedestino || "").trim() !== "";

  const practiceLines = useMemo(() => {
    const lines = [];
    if (firstResponder === "chatbot") {
      lines.push(
        t("channelForm.twilioPhone.practiceChatbotNamed", {
          name: chatbotName || t("channelForm.twilioPhone.practiceUnnamedChatbot", "o chatbot escolhido"),
          defaultValue:
            "O cliente fala primeiro com {{name}} (teclas e voz). Se o fluxo passar para uma pessoa, toca nela. Se passar para a equipe, usa a fila abaixo.",
        })
      );
    } else if (firstResponder === "agent") {
      lines.push(
        t("channelForm.twilioPhone.practiceAgentNamed", {
          name: agentName || t("channelForm.twilioPhone.practiceUnnamedAgent", "o agente de IA"),
          defaultValue:
            "O cliente fala primeiro com {{name}}. Se a IA passar para uma pessoa, toca nela. Se passar para a equipe, usa a fila abaixo.",
        })
      );
    } else if (firstResponder === "human") {
      lines.push(
        t("channelForm.twilioPhone.practiceHumanNamed", {
          name: firstHumanName || t("channelForm.twilioPhone.practiceUnnamedHuman", "o atendente escolhido"),
          wait: waitLabel,
          defaultValue:
            "A ligação toca para {{name}}: no app (aba aberta) ou no celular do perfil. Se estiver em outra ligação, o cliente espera na fila pessoal dele até {{wait}}.",
        })
      );
    } else {
      lines.push(
        t(
          "channelForm.twilioPhone.practiceNone",
          "A ligação já entra na fila da equipe — ninguém de chatbot ou IA atende primeiro."
        )
      );
    }

    if (hasQueuePool) {
      if (firstResponder === "human") {
        lines.push(
          t(
            "channelForm.twilioPhone.practiceQueueAfterHuman",
            "A fila da equipe só entra se alguém transferir a solicitação depois (chat ou automação). Não toca nela no início."
          )
        );
      }
      if (dispatchMode === "auto") {
        lines.push(
          t(
            "channelForm.twilioPhone.practiceQueueAuto",
            "Na fila, o app de quem estiver livre e com a aba aberta toca sozinho. Sem app, toca o celular do perfil."
          )
        );
      } else {
        lines.push(
          t(
            "channelForm.twilioPhone.practiceQueueClaim",
            "Na fila, o cliente espera com música. Quem estiver livre clica em Falar agora no painel."
          )
        );
      }
      lines.push(
        t("channelForm.twilioPhone.practiceQueueTimeout", {
          wait: waitLabel,
          defaultValue:
            "Se ninguém atender em {{wait}}, o cliente pode deixar recado na caixa postal.",
        })
      );
    } else if (firstResponder === "human") {
      lines.push(
        t("channelForm.twilioPhone.practiceHumanNoTeam", {
          wait: waitLabel,
          defaultValue:
            "Não há fila da equipe. Se este atendente não puder falar, o cliente espera até {{wait}} e depois vai à caixa postal.",
        })
      );
    } else {
      lines.push(
        t(
          "channelForm.twilioPhone.practiceQueueEmpty",
          "Falta escolher quem atende na equipe. Sem isso a ligação não tem destino humano e vai à caixa postal."
        )
      );
    }
    if (showLastResort && lastResortFilled) {
      lines.push(
        t(
          "channelForm.twilioPhone.practiceLastResort",
          "Há um número externo de emergência. Só é usado se a ligação não tiver ninguém do XPro para tocar — não substitui a fila nem a caixa postal."
        )
      );
    }
    return lines;
  }, [
    agentName,
    chatbotName,
    dispatchMode,
    firstHumanName,
    firstResponder,
    hasQueuePool,
    lastResortFilled,
    showLastResort,
    t,
    waitLabel,
  ]);

  const step2Title =
    firstResponder === "none"
      ? t("channelForm.twilioPhone.step2TitleNone", "Quem da equipe vai atender")
      : firstResponder === "human"
        ? t(
            "channelForm.twilioPhone.step2TitleHuman",
            "Outras pessoas (opcional)"
          )
        : t(
            "channelForm.twilioPhone.step2TitleHandoff",
            "Quem atende se for para a equipe"
          );

  const step2Helper =
    firstResponder === "none"
      ? t(
          "channelForm.twilioPhone.step2HelperNone",
          "Obrigatório. A ligação cai direto nesta equipe."
        )
      : firstResponder === "human"
        ? t("channelForm.twilioPhone.step2HelperHuman", {
            name:
              firstHumanName ||
              t("channelForm.twilioPhone.practiceUnnamedHuman", "o atendente da etapa 1"),
            defaultValue:
              "No início toca só em {{name}}. Marque mais gente só se, depois, outra pessoa puder assumir pela fila.",
          })
        : t(
            "channelForm.twilioPhone.step2HelperHandoff",
            "Obrigatório. Se o fluxo pedir a equipe (e não uma pessoa específica), é esta lista que atende."
          );

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {t("channelForm.twilioPhone.attendanceTitle", "Como a ligação é atendida")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            "channelForm.twilioPhone.attendanceIntro",
            "Primeiro quem fala com o cliente. Depois, se precisar de humano, quem da equipe pode atender."
          )}
        </Typography>

        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Chip size="small" label="1" color="primary" />
            <Typography variant="subtitle2" fontWeight={700}>
              {t("channelForm.twilioPhone.step1Title", "Quem fala primeiro com o cliente?")}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              "channelForm.twilioPhone.step1Helper",
              "Escolha só um. Os outros campos somem para não misturar regras."
            )}
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={firstResponder}
              onChange={(e) => onFirstResponderRadio(e.target.value)}
            >
              <FormControlLabel
                value="chatbot"
                disabled={!hasChatbotOptions}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t("channelForm.twilioPhone.destChatbot", "Menu automático (chatbot)")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {!hasChatbotOptions
                        ? t(
                            "channelForm.twilioPhone.destChatbotDisabled",
                            "Não há chatbot com menu automático neste workspace."
                          )
                        : firstResponder === "chatbot" && chatbotName
                          ? t("channelForm.twilioPhone.destChatbotHintPicked", {
                              name: chatbotName,
                              defaultValue:
                                "{{name}} atende primeiro. O fluxo dele decide o próximo passo.",
                            })
                          : t(
                              "channelForm.twilioPhone.destChatbotHint",
                              "O cliente ouve o menu e fala ou aperta teclas. O fluxo decide se passa para uma pessoa ou para a equipe."
                            )}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 0.5 }}
              />
              <FormControlLabel
                value="agent"
                disabled={!hasAgentOptions}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t("channelForm.twilioPhone.destAgent", "Agente de IA")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {!hasAgentOptions
                        ? t(
                            "channelForm.twilioPhone.destAgentDisabled",
                            "Não há agente de IA ativo neste workspace."
                          )
                        : firstResponder === "agent" && agentName
                          ? t("channelForm.twilioPhone.destAgentHintPicked", {
                              name: agentName,
                              defaultValue:
                                "{{name}} fala primeiro. Se passar para humano, toca na pessoa do fluxo ou na equipe abaixo.",
                            })
                          : t(
                              "channelForm.twilioPhone.destAgentHint",
                              "A IA conversa. Se passar para humano, toca na pessoa do fluxo ou na equipe abaixo."
                            )}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 0.5 }}
              />
              <FormControlLabel
                value="human"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t("channelForm.twilioPhone.destHuman", "Uma pessoa")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {firstResponder === "human" && firstHumanName
                        ? t("channelForm.twilioPhone.destHumanHintPicked", {
                            name: firstHumanName,
                            defaultValue:
                              "Toca para {{name}}: app aberto ou celular do perfil. Se estiver em outra ligação, o cliente espera na fila pessoal.",
                          })
                        : t(
                            "channelForm.twilioPhone.destHumanHint",
                            "Toca só nesta pessoa: no app (aba aberta) ou no celular do perfil."
                          )}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 0.5 }}
              />
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t("channelForm.twilioPhone.destNone", "Direto para a equipe")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "channelForm.twilioPhone.destNoneHint",
                        "Sem menu, IA ou pessoa fixa. A ligação já espera na equipe abaixo."
                      )}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start" }}
              />
            </RadioGroup>
          </FormControl>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {firstResponder === "chatbot" && (
              <Grid item xs={12} md={8}>
                <Autocomplete
                  id="twilio-phone-routing-chatbot"
                  fullWidth
                  autoComplete="off"
                  options={chatbotMenuOptions}
                  loading={loadingUsers}
                  value={selectedChatbotOption}
                  onChange={(e, newValue) => {
                    const next = newValue == null ? null : String(newValue.id);
                    idChatbotRef.current = next;
                    if (next != null && next !== "") {
                      idAgenteRef.current = null;
                      setFormData((prev) => ({
                        ...prev,
                        _voice_first_responder: "chatbot",
                        id_chatbot: next,
                        id_agente: null,
                        idusuarios: [],
                        idusuario: Number(next),
                      }));
                    } else {
                      idChatbotRef.current = null;
                      setFormData((prev) => ({
                        ...prev,
                        id_chatbot: null,
                        idusuario: null,
                      }));
                    }
                  }}
                  isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                  getOptionLabel={(o) => o?.name ?? o?.nome ?? `Chatbot #${o?.id ?? ""}`}
                  noOptionsText={
                    loadingUsers
                      ? t("channelForm.twilioPhone.loadingUsers", "Carregando…")
                      : t(
                          "channelForm.twilioPhone.noChatbots",
                          "Nenhum chatbot com menu automático neste workspace"
                        )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={t("channelForm.twilioPhone.pickChatbot", "Qual chatbot?")}
                      helperText={t(
                        "channelForm.twilioPhone.pickChatbotHelper",
                        "É ele que recebe a ligação. O menu dele escolhe o próximo passo: uma pessoa ou a equipe abaixo."
                      )}
                      error={hasChatbotRouting === false && firstResponder === "chatbot"}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingUsers ? (
                              <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {firstResponder === "agent" && (
              <Grid item xs={12} md={8}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t("channelForm.twilioPhone.pickAgent", "Qual agente de IA?")}
                  value={agentSelectValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    idAgenteRef.current = v === "" ? null : v;
                    if (v === "") {
                      setFormData((prev) => ({ ...prev, id_agente: null }));
                      return;
                    }
                    idChatbotRef.current = null;
                    setFormData((prev) => ({
                      ...prev,
                      _voice_first_responder: "agent",
                      id_agente: v,
                      id_chatbot: null,
                      idusuarios: [],
                      idusuario: null,
                    }));
                  }}
                  helperText={t(
                    "channelForm.twilioPhone.pickAgentHelper",
                    "É ele que conversa no início. Se transferir sem escolher pessoa, a equipe abaixo atende."
                  )}
                  error={!agentSelectValue}
                >
                  <MenuItem value="">{noneLabel}</MenuItem>
                  {activeAgents.map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>
                      {a.name ?? a.nome ?? `Agente #${a.id}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {firstResponder === "human" && (
              <Grid item xs={12} md={8}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t("channelForm.twilioPhone.pickHuman", "Qual atendente?")}
                  value={firstHumanId}
                  onChange={(e) => {
                    const id = Number(e.target.value) || 0;
                    idChatbotRef.current = null;
                    idAgenteRef.current = null;
                    setFormData((prev) => ({
                      ...prev,
                      _voice_first_responder: "human",
                      id_chatbot: null,
                      id_agente: null,
                      idusuarios: id > 0 ? [id] : [],
                      idusuario: id > 0 ? id : null,
                    }));
                  }}
                  helperText={t(
                    "channelForm.twilioPhone.pickHumanHelper",
                    "Toca só nesta pessoa. Ela precisa do app aberto ou do celular no perfil."
                  )}
                  error={!hasHumanRouting}
                >
                  <MenuItem value="">{noneLabel}</MenuItem>
                  {loadingUsers && <MenuItem value="">Carregando...</MenuItem>}
                  {!loadingUsers &&
                    humanOptions.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {titleOf(u)}
                        {u?.email ? ` · ${u.email}` : ""}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: queueNeeded && !hasQueuePool ? "warning.main" : "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Chip size="small" label="2" color="primary" />
            <Typography variant="subtitle2" fontWeight={700}>
              {step2Title}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {step2Helper}
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={t("channelForm.twilioPhone.queueUsers", "Pessoas da equipe")}
                value={queueUserIds}
                onChange={(e) => {
                  const val = e.target.value;
                  const ids = Array.isArray(val) ? val.map((v) => Number(v)) : [];
                  setFormData((prev) => ({ ...prev, voice_queue_user_ids: ids }));
                }}
                helperText={t(
                  "channelForm.twilioPhone.queueUsersHelper",
                  "Opcional se já houver um cargo ao lado. Entram se estiverem logados e sem outra ligação."
                )}
                error={queueNeeded && !hasQueuePool}
                InputLabelProps={{ required: queueNeeded && !hasQueuePool }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const ids = Array.isArray(selected) ? selected.map(Number) : [];
                    const names = humanOptions
                      .filter((u) => ids.includes(Number(u.id)))
                      .map((u) => titleOf(u));
                    return multiSelectRenderValue(ids, names, noneLabel);
                  },
                }}
              >
                {loadingUsers && <MenuItem value="">Carregando...</MenuItem>}
                {!loadingUsers &&
                  humanOptions.map((u) => {
                    const checked = queueUserIds.includes(Number(u.id));
                    return (
                      <MenuItem key={u.id} value={u.id}>
                        <Checkbox checked={checked} />
                        <ListItemText primary={titleOf(u)} secondary={u?.email || undefined} />
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={t("channelForm.twilioPhone.queueCargos", "Ou um cargo inteiro")}
                value={queueCargoIds}
                onChange={(e) => {
                  const val = e.target.value;
                  const ids = Array.isArray(val) ? val.map((v) => Number(v)) : [];
                  setFormData((prev) => ({ ...prev, voice_queue_cargo_ids: ids }));
                }}
                helperText={t(
                  "channelForm.twilioPhone.queueCargosHelper",
                  "Basta este cargo, ou as pessoas ao lado. Quem tiver o cargo entra na equipe."
                )}
                error={queueNeeded && !hasQueuePool}
                InputLabelProps={{ required: queueNeeded && !hasQueuePool }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const ids = Array.isArray(selected) ? selected.map(Number) : [];
                    const names = queuePositions
                      .filter((p) => ids.includes(Number(p.id)))
                      .map((p) => p.nome || p.name || `#${p.id}`);
                    return multiSelectRenderValue(ids, names, noneLabel);
                  },
                }}
              >
                {queuePositionsLoading && <MenuItem value="">Carregando...</MenuItem>}
                {!queuePositionsLoading &&
                  queuePositions.map((p) => {
                    const checked = queueCargoIds.includes(Number(p.id));
                    return (
                      <MenuItem key={p.id} value={p.id}>
                        <Checkbox checked={checked} />
                        <ListItemText primary={p.nome || p.name || `#${p.id}`} />
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>

            {showDispatch ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={t(
                    "channelForm.twilioPhone.queueDispatchMode",
                    "Como a equipe pega a ligação"
                  )}
                  value={dispatchMode}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      voice_queue_dispatch_mode: e.target.value,
                    }))
                  }
                  helperText={
                    dispatchMode === "claim"
                      ? t(
                          "channelForm.twilioPhone.queueDispatchClaimHelper",
                          "O cliente espera. Quem quiser atende no painel (Falar agora)."
                        )
                      : t(
                          "channelForm.twilioPhone.queueDispatchAutoHelper",
                          "Toca sozinho em quem estiver livre, com o app aberto ou celular no perfil."
                        )
                  }
                >
                  <MenuItem value="auto">
                    {t(
                      "channelForm.twilioPhone.queueDispatchAuto",
                      "Tocar sozinho em quem estiver livre"
                    )}
                  </MenuItem>
                  <MenuItem value="claim">
                    {t(
                      "channelForm.twilioPhone.queueDispatchClaim",
                      "Esperar alguém clicar em Falar agora"
                    )}
                  </MenuItem>
                </TextField>
              </Grid>
            ) : null}

            {showWait ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t(
                    "channelForm.twilioPhone.queueMaxWait",
                    "Quanto tempo o cliente espera"
                  )}
                  value={maxWaitSec}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      voice_queue_max_wait_sec:
                        Number.isFinite(n) && n > 0 ? Math.min(n, 3600) : 300,
                    }));
                  }}
                  helperText={
                    firstResponder === "human" && !hasQueuePool
                      ? t("channelForm.twilioPhone.queueMaxWaitHelperHuman", {
                          wait: waitLabel,
                          defaultValue:
                            "Se o atendente estiver ocupado, o cliente espera {{wait}} e depois vai à caixa postal.",
                        })
                      : t("channelForm.twilioPhone.queueMaxWaitHelper", {
                          wait: waitLabel,
                          defaultValue:
                            "Depois de {{wait}} sem atendimento: caixa postal. O usual é 2 a 10 minutos.",
                        })
                  }
                  inputProps={{ min: 30, max: 3600, step: 30 }}
                />
              </Grid>
            ) : null}

            {showHoldExperience ? (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    {t(
                      "channelForm.twilioPhone.holdExperienceTitle",
                      "Enquanto o cliente espera"
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {firstResponder === "human" && !hasQueuePool
                      ? t(
                          "channelForm.twilioPhone.holdExperienceHelperHuman",
                          "Só entra se o atendente da etapa 1 estiver em outra ligação."
                        )
                      : t(
                          "channelForm.twilioPhone.holdExperienceHelper",
                          "Música e avisos na linha, até alguém atender ou acabar o tempo."
                        )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(formData.voice_queue_hold_announce_eta ?? 1) !== 0}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            voice_queue_hold_announce_eta: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label={t(
                      "channelForm.twilioPhone.holdAnnounceEta",
                      "Avisar o tempo estimado (só se já houver várias ligações recentes neste canal)"
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(formData.voice_queue_hold_dtmf_mailbox ?? 1) !== 0}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            voice_queue_hold_dtmf_mailbox: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label={t(
                      "channelForm.twilioPhone.holdDtmfMailbox",
                      "Permitir tecla 2: deixar recado agora"
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(formData.voice_queue_hold_dtmf_callback ?? 1) !== 0}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            voice_queue_hold_dtmf_callback: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label={t(
                      "channelForm.twilioPhone.holdDtmfCallback",
                      "Permitir tecla 9: pedir para ligarmos depois"
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <VoiceHoldMusicPicker
                    t={t}
                    value={formData.voice_queue_hold_music_url || ""}
                    onChange={(next) =>
                      setFormData((prev) => ({
                        ...prev,
                        voice_queue_hold_music_url: next,
                      }))
                    }
                  />
                </Grid>
              </>
            ) : null}

            {showLastResort ? (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t(
                  "channelForm.twilioPhone.destinationNumber",
                  "Número externo de emergência (opcional)"
                )}
                value={formData.telefonedestino || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    telefonedestino: e.target.value,
                  }))
                }
                helperText={
                  hasQueuePool
                    ? t(
                        "channelForm.twilioPhone.destinationNumberHelperQueue",
                        "A equipe acima é o destino. Este número só entra em falha — não evita a fila nem a caixa postal."
                      )
                    : t(
                        "channelForm.twilioPhone.destinationNumberHelper",
                        "Não substitui a equipe. Este número só entra se a ligação não tiver ninguém do XPro para tocar."
                      )
                }
              />
            </Grid>
            ) : null}
          </Grid>

          {queueNeeded && !hasQueuePool ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {t(
                "channelForm.twilioPhone.queueEmptyWarning",
                "Escolha ao menos uma pessoa ou um cargo. Sem equipe, não há humano para atender."
              )}
            </Alert>
          ) : null}
          {firstResponder === "human" && !hasQueuePool ? (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {t(
                "channelForm.twilioPhone.humanNoQueueHint",
                "Tudo bem deixar a equipe vazia: no início só toca no atendente da etapa 1."
              )}
            </Alert>
          ) : null}
        </Box>

        <Alert severity="info" icon={false}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
            {t("channelForm.twilioPhone.practiceTitle", "O que vai acontecer")}
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
            {practiceLines.map((line) => (
              <Typography component="li" variant="body2" key={line}>
                {line}
              </Typography>
            ))}
          </Stack>
        </Alert>
      </Paper>
    </Grid>
  );
}

export { deriveVoiceFirstResponder, validateTwilioPhoneRoutingQueue };
